<template>
    <div class="d-flex align-items-start">
        <envelope-icon
            v-bind:type="iconType"
            class="me-2"
            v-bind:center="false" />
        <div>
            <div class="fw-bold">
                {{ itemDefinition.Label }}
            </div>
            <div v-if="Boolean(displayName)">
                {{ displayName }}
            </div>
            <div
                v-else
                class="text-body-secondary fst-italic">
                {{ placeholderText }}
            </div>
        </div>
    </div>
</template>
<script>
    import { ENVELOPE_ITEM_TYPE } from "ESignatures/EditEnvelope/editEnvelopeStore";
    import { KEYS, NAMESPACE } from "./i18n/envelopeItemStatus.i18n";
    import { mapI18n } from "Util/i18n";
    import envelopeIcon, { getIconTypeForItemType } from "ESignatures/EditEnvelope/Components/envelopeIcon.vue";

    export default {
        components: {
            envelopeIcon
        },
        props: {
            item: { type: Object, required: true },
            itemDefinition: { type: Object, required: true },
            getAttachmentByAttachmentIdFn: { type: Function, required: true },
            getContentLibraryItemByIdFn: { type: Function, required: true }
        },
        computed: {
            itemType() {
                return this.itemDefinition.Type;
            },
            displayName() {
                switch (this.itemType) {
                    case ENVELOPE_ITEM_TYPE.Attachment:
                        return this._getAttachmentName();
                    case ENVELOPE_ITEM_TYPE.ContentItem:
                        return this._getContentItemName();
                    case ENVELOPE_ITEM_TYPE.DocumentTemplate:
                        return this._getGeneratedDocumentName();
                    default:
                        throw `Unrecognized item type ${this.itemType}`;
                }
            },
            placeholderText() {
                switch (this.itemType) {
                    // There's no valid, expected case where we'd show a placeholder for an attachment- or content-item-
                    // type envelope item. If the envelope definition contains an optional attachment and there was no
                    // file uploaded for the attachment when the envelope was created, we don't create an envelope_item
                    // to track that. If the envelope definition contains a required attachment, there must have been an
                    // attachment uploaded when the envelope was created. In other words, there's no expected case where
                    // we could have an envelope_item representing an attachment but not have an actual file for that
                    // attachment, so we don't need a placeholder
                    // Similar for content library items - if we have an envelope item pointing to a content item, we
                    // should be able to find the content item, so there's no expected need for a placeholder.
                    case ENVELOPE_ITEM_TYPE.Attachment:
                    case ENVELOPE_ITEM_TYPE.ContentItem:
                        return null;
                    case ENVELOPE_ITEM_TYPE.DocumentTemplate:
                        return this.noFileUploadedText;
                    default:
                        throw `Invalid item type ${this.itemType}`;
                }
            },
            iconType() {
                return getIconTypeForItemType(this.itemDefinition.Type);
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        methods: {
            _getAttachmentName() {
                const attachment = this.getAttachmentByAttachmentIdFn(this.item.AttachmentId);
                return attachment?.FileName ?? null;
            },
            _getContentItemName() {
                const contentItem = this.getContentLibraryItemByIdFn(this.itemDefinition.ContentItemId);
                return contentItem?.Name ?? null;
            },
            _getGeneratedDocumentName() {
                const document = this.item.Document;
                return document?.FileName ?? null;
            }
        }
    };
</script>
